import React, { useState, useContext } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { useNavigate } from 'react-router-dom';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const SocialButtons = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const navigate = useNavigate();
  const auth = useContext(CONTEXTS.AuthContext);
  const api = useContext(CONTEXTS.APIContext);
  const authController = new CONTROLLERS.AuthController(api);

  // Google Sign In - using authorization code flow for web
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    redirect_uri: 'http://localhost:3001',
    onSuccess: async (codeResponse) => {
      try {
        setIsGoogleLoading(true);
        console.log('Google sign-in successful!', codeResponse);

        try {
          // Send the authorization code to our backend
          // Backend will exchange it for tokens and verify
          const res = await authController.signInWithGoogle(codeResponse.code, true);

          // Sign in with the tokens received from our backend
          await auth.signIn(res.data.accessToken, res.data.refreshToken);
          
          console.log('Google authentication successful');
          
          // Navigate to home
          navigate('/', { replace: true });
        } catch (backendError) {
          console.error('Backend authentication error:', backendError);
          alert('Failed to authenticate with the server. Please try again later.');
        }
      } catch (error) {
        console.error('Google Sign-In Error:', error);
        alert('Google Sign-In failed. Please try again.');
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Sign-In Error:', error);
      alert('Google Sign-In failed. Please try again.');
      setIsGoogleLoading(false);
    },
  });

  // Facebook Sign In
  const handleFacebookResponse = async (response) => {
    if (response.accessToken) {
      try {
        setIsFacebookLoading(true);
        console.log('Facebook sign-in successful!', response);

        try {
          // Send the access token to our backend
          const res = await authController.signInWithFacebook(response.accessToken, true);

          console.log('Backend response:', res);
          
          // Sign in with the tokens received from our backend
          await auth.signIn(res.data.accessToken, res.data.refreshToken);
          
          console.log('Facebook authentication successful');
          
          // Navigate to home
          navigate('/', { replace: true });
        } catch (backendError) {
          console.error('Facebook auth backend error:', backendError);
          alert('Failed to authenticate with the server. Please try again later.');
        }
      } catch (error) {
        console.error('Facebook login error:', error);
        alert('Facebook Sign-In failed. Please try again.');
      } finally {
        setIsFacebookLoading(false);
      }
    } else {
      console.log('Facebook login cancelled or failed');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Google Sign In Button */}
      <Button
        variant="outlined"
        fullWidth
        onClick={() => googleLogin()}
        disabled={isGoogleLoading || isFacebookLoading}
        sx={{
          py: 1.5,
          borderColor: '#e0e0e0',
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': {
            borderColor: '#bdbdbd',
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        {isGoogleLoading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              style={{ width: 20, height: 20 }}
            />
            Continue with Google
          </>
        )}
      </Button>

      {/* Facebook Sign In Button */}
      <FacebookLogin
        appId="861270729772072" // Facebook App ID (matches React Native app)
        autoLoad={false}
        fields="name,email,picture"
        callback={handleFacebookResponse}
        render={(renderProps) => (
          <Button
            variant="outlined"
            fullWidth
            onClick={renderProps.onClick}
            disabled={isGoogleLoading || isFacebookLoading}
            sx={{
              py: 1.5,
              borderColor: '#e0e0e0',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {isFacebookLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <img
                  src="https://www.facebook.com/images/fb_icon_325x325.png"
                  alt="Facebook"
                  style={{ width: 20, height: 20 }}
                />
                Continue with Facebook
              </>
            )}
          </Button>
        )}
      />
    </Box>
  );
};

export default SocialButtons;
